<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import {
    GUIDE_LEAD_CHIPS,
    chipValues,
  } from '@/domain/reservations/reservation-guide';
  import { guideDurationLabel } from './reservation-guide-copy';

  /**
   * The finish screen's «Reglas» row: one sentence per rule and an inline
   * editor for days ahead, minimum notice and confirmation mode (§3.6). The
   * open editor counts as unsaved work for the leave guard.
   */
  const MAX_ADVANCE_DAYS = 365;
  const context = useReservationGuide();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  const days = ref(1);
  const lead = ref(0);
  const autoConfirm = ref(true);

  const settings = computed(
    () => context.configuration.value?.settings ?? null
  );
  const busy = computed(() => context.busy.value);
  const editing = computed(() => context.rulesEditing.value);
  const sentences = computed(() => {
    const value = settings.value;
    if (!value) return [];
    return [
      tr('reservations.guide.rules.advance', {
        days: String(
          proxy.$tc('reservations.guide.rules.days', value.maxAdvanceDays, {
            count: value.maxAdvanceDays,
          })
        ),
      }),
      value.minLeadMin > 0
        ? tr('reservations.guide.rules.lead', {
            lead: guideDurationLabel(value.minLeadMin, tr),
          })
        : tr('reservations.guide.rules.leadNone'),
      tr(
        value.autoConfirm
          ? 'reservations.guide.rules.autoConfirm'
          : 'reservations.guide.rules.manualConfirm'
      ),
      tr('reservations.guide.rules.party', {
        min: value.minPartyOnline,
        max: value.maxPartyOnline,
      }),
    ];
  });
  const leadChips = computed(() => chipValues(GUIDE_LEAD_CHIPS, lead.value));
  const confirmItems = computed(() => [
    { id: 'auto', label: tr('reservations.guide.rules.confirmAuto') },
    { id: 'manual', label: tr('reservations.guide.rules.confirmManual') },
  ]);

  function leadLabel(minutes: number): string {
    return minutes === 0
      ? tr('reservations.guide.rules.leadNoneOption')
      : guideDurationLabel(minutes, tr);
  }
  function open() {
    const value = settings.value;
    if (!value || busy.value) return;
    days.value = value.maxAdvanceDays;
    lead.value = value.minLeadMin;
    autoConfirm.value = value.autoConfirm;
    context.rulesEditing.value = true;
  }
  function cancel() {
    context.rulesEditing.value = false;
  }
  function selectConfirm(id: string) {
    autoConfirm.value = id === 'auto';
  }
  async function save() {
    if (busy.value) return;
    const saved = await context.savePatch({
      maxAdvanceDays: days.value,
      minLeadMin: lead.value,
      autoConfirm: autoConfirm.value,
    });
    if (saved && alive) context.rulesEditing.value = false;
  }
  function openGeneral() {
    void context.exit({ tab: 'settings', section: 'general' });
  }
</script>

<template>
  <div class="reservations-guide-rules">
    <div class="summary-row">
      <div class="row-text">
        <span class="row-label">{{
          $t('reservations.guide.summary.rules')
        }}</span>
        <span class="row-value">{{ sentences.join(' ') }}</span>
      </div>
      <PosUiButton
        color="neutral"
        variant="ghost"
        :aria-label="`${$t('reservations.guide.change')} · ${$t('reservations.guide.summary.rules')}`"
        :aria-expanded="editing ? 'true' : 'false'"
        aria-controls="reservations-guide-rules-editor"
        :disabled="busy || editing"
        @click="open"
        >{{ $t('reservations.guide.change') }}</PosUiButton
      >
    </div>
    <div
      v-if="editing"
      id="reservations-guide-rules-editor"
      class="rules-editor"
      role="group"
      :aria-label="$t('reservations.guide.summary.rules')"
    >
      <!-- Plain captions: a <label> would forward taps to the stepper's «−». -->
      <div class="rules-field">
        <span class="field-caption">{{
          $t('reservations.guide.rules.daysLabel')
        }}</span>
        <PosUiStepper
          :value="days"
          :min="1"
          :max="MAX_ADVANCE_DAYS"
          size="md"
          editable
          :disabled="busy"
          :aria-label="$t('reservations.guide.rules.daysLabel')"
          @input="days = $event"
        />
      </div>
      <div class="rules-field">
        <span class="field-caption">{{
          $t('reservations.guide.rules.leadLabel')
        }}</span>
        <div
          class="chip-row"
          role="group"
          :aria-label="$t('reservations.guide.rules.leadLabel')"
        >
          <PosUiChip
            v-for="minutes in leadChips"
            :key="minutes"
            :active="minutes === lead"
            :disabled="busy"
            @click="lead = minutes"
            >{{ leadLabel(minutes) }}</PosUiChip
          >
        </div>
      </div>
      <div class="rules-field">
        <span class="field-caption">{{
          $t('reservations.guide.rules.confirmLabel')
        }}</span>
        <PosUiSegmented
          :value="autoConfirm ? 'auto' : 'manual'"
          :items="confirmItems"
          :disabled="busy"
          :aria-label="$t('reservations.guide.rules.confirmLabel')"
          @input="selectConfirm"
        />
      </div>
      <div class="rules-actions">
        <PosUiButton
          color="neutral"
          variant="outline"
          :disabled="busy"
          @click="save"
          >{{ $t('reservations.guide.done') }}</PosUiButton
        >
        <PosUiButton
          color="neutral"
          variant="ghost"
          :disabled="busy"
          @click="cancel"
          >{{ $t('reservations.guide.rules.cancel') }}</PosUiButton
        >
        <PosUiButton
          class="more-button"
          color="neutral"
          variant="ghost"
          icon="open-in-new"
          :disabled="busy"
          @click="openGeneral"
          >{{ $t('reservations.guide.rules.more') }}</PosUiButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-rules {
    @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
    .summary-row {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-4 tw-py-3;
    }
    .row-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5 tw-pt-2.5;
    }
    .row-label {
      @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .row-value {
      @apply tw-break-words tw-text-sm tw-text-neutral-900;
    }
    .rules-editor {
      @apply tw-mx-4 tw-mb-4 tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-canvas tw-p-4;
    }
    .rules-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;
      .field-caption {
        @apply tw-text-sm tw-font-medium tw-text-neutral-900;
      }
    }
    .chip-row {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .rules-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .more-button {
      @apply sm:tw-ml-auto;
    }
  }
</style>
